import {
  ExpertNoteIdentifier,
  FieldedStatus,
  MissionAssessmentEffectCostDtoCurrencyEnum,
  MissionAssessmentEffectTimeUnit,
  MissionAssessmentFieldName,
  MissionAssessmentInterType,
  VerificationConfidence,
} from '@@types/api.types';
import type {
  MissionAssessmentDataDto,
  MissionAssessmentEffectCostDto,
  MissionAssessmentEffectCreateDto,
  MissionAssessmentEffectDataDto,
  MissionAssessmentEffectTimeDto,
  MissionAssessmentExpertNoteDto,
  MissionAssessmentHurdlesDto,
  MissionAssessmentInterCreateDto,
  MissionAssessmentServiceCreateDto,
  MissionAssessmentTechnologyUseCreateDto,
} from '@@types/api.types';
import {
  MA_FULL_SNAPSHOT_TEXT_MAX_LENGTH,
  MA_FULL_SNAPSHOT_VALIDATION_MESSAGES,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
  NAME_VALIDATION_MESSAGES,
  SHORT_DESCRIPTION_MAX_LENGTH,
  SHORT_DESCRIPTION_VALIDATION_MESSAGES,
} from '@azymmetric/shared/validation/validation.constants';
import { zArrayWithoutEmptyObjectSchema, zCoercedOptional, zFilteredStringArray } from '@utils/zodv4.util';
import { z } from 'zod/v4';

/**
 * Base schema for expert note structure without transforms.
 * Used for accessing .shape properties in form validation.
 */
export const missionAssessmentExpertNoteBaseSchema = z
  .object({
    content: z.string().optional(),
    identifier: z.enum(ExpertNoteIdentifier),
  })
  .strip();

/**
 * Schema for validating expert notes in mission assessments (form input).
 * Allows optional content for form flexibility and automatically removes empty content.
 */
export const missionAssessmentExpertNoteFormSchema = missionAssessmentExpertNoteBaseSchema.transform(
  (data): MissionAssessmentExpertNoteDto | null => {
    // Check if content exists and is not empty after trimming
    const trimmedContent = data.content?.trim();
    if (!trimmedContent || trimmedContent === '') {
      return null;
    }
    return {
      content: trimmedContent,
      identifier: data.identifier,
    };
  },
);

/**
 * Schema for validating expert notes in drafts - preserves empty notes for partial saves
 */
export const missionAssessmentExpertNoteDraftSchema = missionAssessmentExpertNoteBaseSchema.transform(
  (data): MissionAssessmentExpertNoteDto => {
    return {
      content: data.content?.trim() || '',
      identifier: data.identifier,
    };
  },
);

/**
 * Schema for validating cost information in mission assessment effects.
 * Validates positive amounts and valid currency enums.
 */
const missionAssessmentEffectCostSchema = z
  .object({
    amount: z.number().nonnegative({ message: 'Cost must be a non-negative number' }).optional(),
    currency: z.enum(MissionAssessmentEffectCostDtoCurrencyEnum),
  })
  .strip() as z.ZodType<MissionAssessmentEffectCostDto, MissionAssessmentEffectCostDto>;

/**
 * Schema for validating time to replace information in mission assessment effects.
 * Ensures positive values and valid time units.
 */
const missionAssessmentEffectTimeSchema = z
  .object({
    unit: z.union([z.enum(MissionAssessmentEffectTimeUnit), z.null()]).optional(),
    value: z
      .union([
        z
          .number({ message: 'Please enter a time value' })
          .int()
          .nonnegative({ message: 'Time must be a non-negative number' }),
        z.null(),
      ])
      .optional(),
  })
  .strip()
  .superRefine((data, ctx) => {
    const hasUnit = data.unit !== undefined && data.unit !== null;
    const hasValue = data.value !== undefined && data.value !== null;

    // Both empty is valid (optional field) - no errors to add
    if (!hasUnit && !hasValue) {
      return;
    }

    // If unit is present but value is missing
    if (hasUnit && !hasValue) {
      ctx.addIssue({
        code: 'custom',
        message: 'Please enter a time value',
        path: ['value'],
      });
    }

    // If value is present but unit is missing
    if (hasValue && !hasUnit) {
      ctx.addIssue({
        code: 'custom',
        message: 'Please select a time unit',
        path: ['unit'],
      });
    }

    // Both present is valid - no errors to add
  }) as z.ZodType<MissionAssessmentEffectTimeDto, MissionAssessmentEffectTimeDto>;

/**
 * Schema for validating effect data in mission assessments.
 * Includes cost, personnel, phases, and descriptive information validation.
 */
export const missionAssessmentEffectDataBaseSchema = z
  .object({
    cost: missionAssessmentEffectCostSchema.optional(),
    costToReplace: missionAssessmentEffectCostSchema.optional(),
    description: zCoercedOptional(
      z.string().max(SHORT_DESCRIPTION_MAX_LENGTH, { message: SHORT_DESCRIPTION_VALIDATION_MESSAGES.max }).optional(),
    ),
    measuresOfPerformance: zCoercedOptional(z.string().optional()),
    measureOfImpact: zCoercedOptional(z.string().optional()),
    personnelRequired: z
      .union([
        z
          .number({ message: 'Please enter a personnel value' })
          .int()
          .nonnegative({ message: 'Personnel must be a non-negative number' }),
        z.null(),
      ])
      .optional(),

    minPhase: zCoercedOptional(
      z.coerce.number({ message: 'Please select a minimum phase' }).int().min(0).max(5).optional(),
    ),
    maxPhase: zCoercedOptional(
      z.coerce.number({ message: 'Please select a maximum phase' }).int().min(0).max(5).optional(),
    ),
    timeToDeploy: missionAssessmentEffectTimeSchema.optional(),
    timeToReplace: missionAssessmentEffectTimeSchema.optional(),
    unit: zCoercedOptional(z.string().optional()),
  })
  .strip()
  .transform((data) => {
    const cost = data.cost?.amount ? { ...data.cost, amount: data.cost.amount } : undefined;

    const costToReplace = data.costToReplace?.amount
      ? { ...data.costToReplace, amount: data.costToReplace.amount }
      : undefined;

    const timeToDeploy =
      data.timeToDeploy?.value && data.timeToDeploy.value > 0
        ? { ...data.timeToDeploy, value: data.timeToDeploy.value }
        : undefined;

    const timeToReplace =
      data.timeToReplace?.value && data.timeToReplace.value > 0
        ? { ...data.timeToReplace, value: data.timeToReplace.value }
        : undefined;

    return {
      ...data,
      cost,
      costToReplace,
      timeToDeploy,
      timeToReplace,
    };
  });

export const missionAssessmentEffectDataSchema = missionAssessmentEffectDataBaseSchema.check((ctx) => {
  const { minPhase, maxPhase } = ctx.value;

  const hasMin = minPhase !== undefined && minPhase !== null;
  const hasMax = maxPhase !== undefined && maxPhase !== null;

  if (hasMin && hasMax && minPhase > maxPhase) {
    ctx.issues.push({
      input: ctx.value,
      code: 'custom',
      message: 'Minimum phase must be less than or equal to maximum phase',
      path: ['minPhase'],
    });
  }
}) as z.ZodType<MissionAssessmentEffectDataDto, MissionAssessmentEffectDataDto>;

/**
 * Schema for validating mission assessment effect creation data.
 * Ensures all required IDs are valid UUIDs and data is properly structured.
 */
export const missionAssessmentEffectCreateSchema = z
  .object({
    data: missionAssessmentEffectDataSchema,
    domainId: z.uuid({ message: 'Please select a domain' }),
    effectId: z.uuid({ message: 'Please select an effect' }),
    secondaryEffects: z.array(z.uuid()),
    targetSystems: z.array(z.uuid()),
    useId: z.uuid({ message: 'Please select an use' }),
  })
  .strip() satisfies z.ZodType<MissionAssessmentEffectCreateDto, MissionAssessmentEffectCreateDto>;

/**
 * Schema for validating hurdles information in mission assessments.
 * Basic field validation only - cross-validation is handled in the main schema.
 */
export const missionAssessmentHurdlesSchema = z
  .object({
    nextMrl: zCoercedOptional(z.string().optional()),
    nextMrlScore: zCoercedOptional(z.coerce.number().int().min(1).max(10).optional()),
    nextTrl: zCoercedOptional(z.string().optional()),
    nextTrlScore: zCoercedOptional(z.coerce.number().int().min(1).max(9).optional()),
    supplyChain: zCoercedOptional(z.string().optional()),
  })
  .strip() satisfies z.ZodType<MissionAssessmentHurdlesDto>;

/**
 * Schema for validating mission assessment data section.
 * Includes expert notes, general notes, hurdles, and phases validation.
 */
export const missionAssessmentDataSchema = z
  .object({
    expertNotes: z.array(missionAssessmentExpertNoteFormSchema).optional(),
    generalNotes: zCoercedOptional(z.string().optional()),
    hurdles: missionAssessmentHurdlesSchema.optional(),
    minPhase: zCoercedOptional(
      z.coerce.number({ message: 'Please select a minimum phase' }).int().min(0).max(5).optional(),
    ),
    maxPhase: zCoercedOptional(
      z.coerce.number({ message: 'Please select a maximum phase' }).int().min(0).max(5).optional(),
    ),
  })
  .strip()
  .transform((data) => {
    const filteredNotes =
      data?.expertNotes?.filter(
        (note): note is MissionAssessmentExpertNoteDto =>
          note !== null && Boolean(note.content) && note.content.trim() !== '',
      ) || [];

    return {
      ...data,
      expertNotes: filteredNotes,
    };
  }) as z.ZodType<MissionAssessmentDataDto, MissionAssessmentDataDto>;

/**
 * Schema for validating mission assessment data section for drafts.
 * Preserves empty expert notes to allow partial saves.
 */
export const missionAssessmentDataDraftSchema = z
  .object({
    expertNotes: z.array(missionAssessmentExpertNoteDraftSchema).optional(),
    generalNotes: zCoercedOptional(z.string().optional()),
    hurdles: missionAssessmentHurdlesSchema.optional(),
    minPhase: zCoercedOptional(
      z.coerce.number({ message: 'Please select a minimum phase' }).int().min(0).max(5).optional(),
    ),
    maxPhase: zCoercedOptional(
      z.coerce.number({ message: 'Please select a maximum phase' }).int().min(0).max(5).optional(),
    ),
  })
  .strip()
  .transform((data) => {
    return {
      ...data,
      expertNotes: data?.expertNotes?.filter((note): note is MissionAssessmentExpertNoteDto =>
        Boolean(note.content && note.identifier),
      ),
    };
  }) as z.ZodType<MissionAssessmentDataDto, MissionAssessmentDataDto>;

/**
 * Schema for validating interoperability/interchangeability entries.
 * Ensures category selection and notes are provided.
 */
export const missionAssessmentInterCreateSchema = z
  .object({
    categoryId: z.uuid({ message: 'Please select a category' }),
    country: zCoercedOptional(z.string().optional()),
    description: zCoercedOptional(
      z.string().max(SHORT_DESCRIPTION_MAX_LENGTH, { message: SHORT_DESCRIPTION_VALIDATION_MESSAGES.max }).optional(),
    ),
    digitalIntegrationIds: zFilteredStringArray(z.array(z.uuid())).optional(),
    fieldedStatus: zCoercedOptional(z.enum(FieldedStatus).optional()),
    mrl: zCoercedOptional(z.number().int().min(1).max(10).optional()),
    name: z
      .string()
      .min(NAME_MIN_LENGTH, { message: NAME_VALIDATION_MESSAGES.min })
      .max(NAME_MAX_LENGTH, { message: NAME_VALIDATION_MESSAGES.max }),
    notes: z.string().min(1, { message: 'Please enter notes' }),
    operationalIntegrationIds: zFilteredStringArray(z.array(z.uuid())).optional(),
    physicalIntegrationIds: zFilteredStringArray(z.array(z.uuid())).optional(),
    priorityRationale: zCoercedOptional(z.string().optional()),
    priorityScore: zCoercedOptional(z.number().int().min(1).max(100).optional()),
    reference: zCoercedOptional(z.string().optional()),
    subcategoryId: z.uuid({ message: 'Please select a subcategory' }),
    trl: zCoercedOptional(z.number().int().min(1).max(9).optional()),
    type: z.enum(MissionAssessmentInterType),
    verificationConfidence: zCoercedOptional(z.enum(VerificationConfidence).optional()),
    verificationNotes: zCoercedOptional(z.string().optional()),
  })
  .strip() satisfies z.ZodType<MissionAssessmentInterCreateDto>;

/**
 * Schema for validating recommended service entries.
 * Ensures service selection and unit specification.
 */
export const missionAssessmentServiceCreateSchema = z
  .object({
    serviceId: z.uuid({ message: 'Please select a service' }),
    unit: zCoercedOptional(z.string().trim().optional()),
  })
  .strip() satisfies z.ZodType<MissionAssessmentServiceCreateDto>;

/**
 * Schema for validating task recommendation entries.
 * Ensures description is provided and within character limit.
 */
export const missionAssessmentTaskRecommendationCreateSchema = z
  .object({
    description: z
      .string()
      .trim()
      .max(MA_FULL_SNAPSHOT_TEXT_MAX_LENGTH, { message: MA_FULL_SNAPSHOT_VALIDATION_MESSAGES.taskRecommendations }),
  })
  .strip();

/**
 * Schema for validating recommended technology use domain entries.
 */
export const missionAssessmentTechnologyUseDomainCreateSchema = z.object({
  domainId: z.uuid({ message: 'Please select a domain' }),
});

/**
 * Schema for validating recommended technology use entries.
 * Each entry contains a use ID, optional description, and list of domains.
 */
export const missionAssessmentTechnologyUseCreateSchema = z
  .object({
    description: zCoercedOptional(
      z
        .string()
        .trim()
        .max(SHORT_DESCRIPTION_MAX_LENGTH, { message: SHORT_DESCRIPTION_VALIDATION_MESSAGES.max })
        .optional(),
    ),
    domains: z.array(missionAssessmentTechnologyUseDomainCreateSchema).min(1, {
      message: 'At least one domain must be selected for each technology use',
    }),
    useId: z.uuid({ message: 'Please select a use' }),
  })
  .strip() satisfies z.ZodType<MissionAssessmentTechnologyUseCreateDto>;

/**
 * Schema for validating AI generation prompt.
 * Prompt is now optional - if not provided, default prompts will be used.
 */
export const missionAssessmentGenerateSchema = z.object({
  prompt: zCoercedOptional(z.string().trim().optional()),
  fieldName: z.enum(MissionAssessmentFieldName),
  technologyId: z.uuid({ message: 'Please select a technology' }),
});
export type MissionAssessmentAiPromptFormData = z.infer<typeof missionAssessmentGenerateSchema>;

/**
 * Deduplicates recommendedTechnologyUses by useId, merging domains.
 * Also deduplicates domains within each tech use by domainId.
 */
const deduplicateRecommendedTechnologyUses = <
  T extends { description?: string; domains: { domainId: string }[]; useId: string },
>(
  techUses: T[] | undefined,
): T[] => {
  if (!techUses || techUses.length === 0) return [];

  const useMap = new Map<string, T>();

  for (const techUse of techUses) {
    const existing = useMap.get(techUse.useId);
    if (existing) {
      // Merge domains, deduplicating by domainId
      const existingDomainIds = new Set(existing.domains.map((d) => d.domainId));
      const newDomains = techUse.domains.filter((d) => !existingDomainIds.has(d.domainId));
      existing.domains = [...existing.domains, ...newDomains];
      // Keep existing description if present, otherwise use new one
      if (!existing.description && techUse.description) {
        existing.description = techUse.description;
      }
    } else {
      // Deduplicate domains within this tech use
      const seenDomainIds = new Set<string>();
      const uniqueDomains = techUse.domains.filter((d) => {
        if (seenDomainIds.has(d.domainId)) return false;
        seenDomainIds.add(d.domainId);
        return true;
      });
      useMap.set(techUse.useId, { ...techUse, domains: uniqueDomains });
    }
  }

  return Array.from(useMap.values());
};

/**
 * Main schema for validating complete mission assessment creation data.
 * Includes all sections with comprehensive validation rules.
 */
export const missionAssessmentBaseSchema = z.object({
  capabilities: z.array(z.uuid()),
  communities: z.array(z.uuid()),
  data: missionAssessmentDataSchema,
  effects: z.array(missionAssessmentEffectCreateSchema),
  inters: zArrayWithoutEmptyObjectSchema(missionAssessmentInterCreateSchema, {
    ignoreKeys: [
      'type',
      'country',
      'digitalIntegrationIds',
      'fieldedStatus',
      'mrl',
      'operationalIntegrationIds',
      'physicalIntegrationIds',
      'priorityRationale',
      'priorityScore',
      'reference',
      'trl',
      'verificationConfidence',
      'verificationNotes',
    ],
  }),
  // z.guid(): seeded Kill Chain ids ("Engage"/"Assess") are uuid-shaped but
  // not RFC-4122 version-valid, so the strict z.uuid() rejects them.
  killChains: zFilteredStringArray(z.array(z.guid())),
  /**
   * @deprecated Use recommendedTechnologyUses instead. This field will be removed in a future version.
   */
  recommendedDomains: zFilteredStringArray(z.array(z.uuid())),
  recommendedServices: zArrayWithoutEmptyObjectSchema(missionAssessmentServiceCreateSchema),
  recommendedTechnologyUses: z
    .array(missionAssessmentTechnologyUseCreateSchema)
    .transform(deduplicateRecommendedTechnologyUses),
  /**
   * @deprecated Use recommendedTechnologyUses instead. This field will be removed in a future version.
   */
  recommendedUses: zFilteredStringArray(z.array(z.uuid())),
  taskRecommendations: zArrayWithoutEmptyObjectSchema(missionAssessmentTaskRecommendationCreateSchema).default([]),
});

export const missionAssessmentFormSchema = missionAssessmentBaseSchema.check((ctx) => {
  const data = ctx.value;

  // Validation always applies when using this schema (submitting as complete)
  if (data.data.hurdles) {
    const { nextMrl, nextMrlScore, nextTrl, nextTrlScore } = data.data.hurdles;
    const hasMrlScore = nextMrlScore !== undefined;
    const hasMrlNotes = nextMrl && nextMrl.trim() !== '';
    const hasTrlScore = nextTrlScore !== undefined;
    const hasTrlNotes = nextTrl && nextTrl.trim() !== '';

    if (hasMrlNotes && !hasMrlScore) {
      ctx.issues.push({
        code: 'custom',
        message: 'Please provide a Score for the next MRL',
        path: ['data', 'hurdles', 'nextMrlScore'],
        input: ctx.value,
      });
    }

    if (hasTrlNotes && !hasTrlScore) {
      ctx.issues.push({
        code: 'custom',
        message: 'Please provide a Score for the next TRL',
        path: ['data', 'hurdles', 'nextTrlScore'],
        input: ctx.value,
      });
    }
  }

  if (!data.capabilities || data.capabilities.length === 0) {
    ctx.issues.push({
      code: 'custom',
      message: 'At least one capability must be selected',
      path: ['capabilities'],
      input: ctx.value,
    });
  }

  if (!data.communities || data.communities.length === 0) {
    ctx.issues.push({
      code: 'custom',
      message: 'At least one community must be selected',
      path: ['communities'],
      input: ctx.value,
    });
  }
});
export type MissionAssessmentFormData = z.infer<typeof missionAssessmentFormSchema>;
